import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, from, of, Subject } from "rxjs";
import { filter, map, switchMap, take, takeUntil } from "rxjs/operators";

import { FeatureFlagService } from "../feature-flag.service";
import { FeatureFlagKeys } from "../feature-flag.models";
import { InteractionTrackingService, InteractionType } from "../interaction-tracking.service";
import { NovaCompanionSettingsService } from "../nova-companion-settings.service";
import { OnboardingStepService } from "../onboarding-step.service";
import { PaymentsService } from "../payments.service";

export const N02_LESSON_ID = "N02";

/** Recorded with N02's completed lesson state - N02's CompletedAt *is* this milestone. */
export const NOVA_FIRST_USE_COMPLETED = "nova_first_use_completed";

/** Copy rows N02.01-N02.09, workbook v2.38 Copy sheet (verbatim; the `*` is a literal asterisk). */
export const N02_COPY = {
  sections: [
    {
      heading: "How Nova works",
      body: "Nova can help you reflect on your experiences, notice patterns and understand how different parts of flip* fit together.",
    },
    {
      heading: "A more personalised conversation",
      body:
        "While enabled, Nova can access your activity across flip*, including your Scores, Plans, Sanctuary " +
        "activity and LEARN progress, and use it to personalise her responses. The more you use flip*, the more " +
        "context she has. You choose what you want to talk about, while this wider context helps Nova respond in a " +
        "way that reflects your experience.",
    },
    {
      heading: "Talk in the way that suits you",
      body: "Type a message, use the microphone to dictate a message, or select Voice Chat for a live spoken conversation.",
    },
    {
      heading: "What Nova can and can't do",
      body:
        "Nova can offer information, practical guidance and a place to think things through. She isn't a " +
        "healthcare professional and can't diagnose, treat or give medical advice.",
    },
  ],
  helpLine: {
    before: "For specific instructions on using flip*, visit the ",
    link: "Help Center",
    after: ".",
  },
};

/**
 * Onboarding lesson N02 "How Nova works" (FLIP-389; spec v2.36 §24.3, workbook v2.38): the Nova
 * full-panel first-use surface, rendered by NovaFirstUsePanelComponent inside the Nova panel in
 * place of the conversation thread, under Nova's normal header. Informational, no Next / Continue /
 * Got it - the four sections all render in one scrollable view under a single close (X).
 *
 * - Eligible on the first opening of Nova from any route while the `onboarding-walkthrough` flag is
 *   on, Nova Companion is enabled and N02's step row is neither completed nor dismissed. A failed
 *   read holds back. Not suppressed because N01 completed. Existing users get no backfill.
 * - The visible close (X) completes N02 permanently and records `nova_first_use_completed`.
 * - Closing Nova while N02 is visible counts as dismissed permanently - it never shows again, and
 *   no milestone is recorded (N05 needs N02 *complete*).
 * - Not generated through the prompt system and never added to the conversation history: it is
 *   plain component state, so the chat service never sees it.
 * - Nova is never opened by this service; the chat calls in once it is already open.
 */
@Injectable({ providedIn: "root" })
export class N02HowNovaWorksService {
  /** Whether the panel is showing right now. */
  readonly visible$ = new BehaviorSubject<boolean>(false);

  /**
   * Whether this opening's eligibility check has finished (whether or not it decided to show). N04
   * waits for it, so its prompt never flashes up just before N02's panel takes over the thread.
   */
  readonly settled$ = new BehaviorSubject<boolean>(false);

  private readonly cancel$ = new Subject<void>();
  /** Resolved (completed or dismissed) this session - its row write may still be in flight. */
  private resolved = false;
  private started = false;

  constructor(
    private router: Router,
    private featureFlagService: FeatureFlagService,
    private paymentsService: PaymentsService,
    private onboardingStepService: OnboardingStepService,
    private companionSettings: NovaCompanionSettingsService,
    private interactionTracking: InteractionTrackingService
  ) {
    // Logout emits false - forget this session's state so a different account gets its own check.
    this.paymentsService.subscriptionActive$.pipe(filter((active) => !active)).subscribe(() => {
      this.resolved = false;
      this.started = false;
      this.cancel();
    });
  }

  get isVisible(): boolean {
    return this.visible$.value;
  }

  get isSettled(): boolean {
    return this.settled$.value;
  }

  /** Call when the Nova chat opens. Shows the panel if N02 is unresolved. */
  onNovaOpened(): void {
    this.cancel();
    this.settled$.next(false);
    if (this.resolved) {
      this.settled$.next(true);
      return;
    }
    this.paymentsService.subscriptionActive$
      .pipe(
        filter((active) => active),
        take(1),
        switchMap(() => from(this.featureFlagService.init())),
        switchMap(() =>
          this.featureFlagService.isEnabled(FeatureFlagKeys.OnboardingWalkthrough) &&
          this.companionSettings.currentlyEnabled
            ? // A failed read (undefined) holds back - a lesson must not guess its way into showing.
              this.onboardingStepService
                .getStep(N02_LESSON_ID)
                .pipe(map((step) => step !== undefined && !step?.completedAt && !step?.dismissed))
            : of(false)
        ),
        take(1),
        takeUntil(this.cancel$)
      )
      .subscribe((eligible) => {
        if (eligible) {
          this.show();
        }
        this.settled$.next(true);
      });
  }

  /** The visible close (X): completes N02 permanently. */
  onClosePressed(): void {
    if (!this.isVisible) {
      return;
    }
    this.resolve({ stepKey: N02_LESSON_ID, completedAt: new Date() });
    this.track("onboarding_lesson_action", "action: close");
    this.track("onboarding_lesson_state_changed", `completed; milestone: ${NOVA_FIRST_USE_COMPLETED}`);
  }

  /** Call when the Nova chat closes. Closing Nova while N02 is visible dismisses it permanently. */
  onNovaClosed(): void {
    this.cancel();
    if (!this.isVisible) {
      return;
    }
    this.resolve({ stepKey: N02_LESSON_ID, dismissed: true });
    this.track("onboarding_lesson_state_changed", "dismissed");
  }

  private show(): void {
    this.visible$.next(true);
    const now = new Date();
    this.onboardingStepService.upsertStep({
      stepKey: N02_LESSON_ID,
      lastViewedAt: now,
      ...(this.started ? {} : { startedAt: now }),
    });
    this.started = true;
    this.track("onboarding_lesson_state_changed", "presented");
  }

  private resolve(update: Parameters<OnboardingStepService["upsertStep"]>[0]): void {
    this.resolved = true;
    this.visible$.next(false);
    this.onboardingStepService.upsertStep(update);
  }

  private cancel(): void {
    this.cancel$.next();
  }

  /** Same shape as CoachMarkService's FLIP-322 lesson events. */
  private track(eventName: string, detail: string): void {
    this.interactionTracking.track({
      screen: this.router.url,
      eventName,
      interactionType: InteractionType.Selection,
      entityType: "OnboardingLesson",
      detail: `lesson: ${N02_LESSON_ID}; ${detail}`,
    });
  }
}
